import { useState } from 'react';
import { 
  Shield, 
  Truck, 
  Flame, 
  Scissors, 
  Home, 
  GraduationCap, 
  Monitor, 
  Calendar, 
  Shirt, 
  Bot as Bottle, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail,
  Package, } from 'lucide-react';

const Services = () => {
  const [expandedService, setExpandedService] = useState<number | null>(0);

  const services = [
    {
      icon: <Home className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Domestic Waste Handling",
      shortDescription: "Efficient and eco-friendly management of household waste with proper segregation and disposal",
      fullDescription: "Our domestic waste handling service provides comprehensive solutions for households to manage their waste effectively. We offer scheduled waste collection, proper segregation at source, and environmentally responsible disposal methods. Our service helps reduce environmental impact while keeping your living spaces clean and hygienic.",
      features: [
        "Doorstep waste collection",
        "Waste segregation at source",
        "Organic waste composting",
        "Recyclable material recovery",
        "Hazardous household waste handling",
        "Monthly collection schedules"
      ],
      pricing: "Starting from UGX 50,000/month",
      image: "/biohaztruck.jpg"
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Medical & Hazardous Waste Disposal",
      shortDescription: "Comprehensive disposal of medical and biohazardous waste with full regulatory compliance",
      fullDescription: "Our medical waste disposal service ensures safe handling and destruction of all types of medical waste including sharps, pathological waste, pharmaceutical waste, and contaminated materials. We follow strict protocols for collection, transportation, treatment, and disposal, providing complete documentation and certificates of destruction.",
      features: [
        "Safe collection and transportation",
        "Pathological waste treatment",
        "Sharps disposal containers",
        "Pharmaceutical waste destruction",
        "Complete documentation tracking",
        "Regulatory compliance certification"
      ],
      pricing: "Starting from UGX 150,000/month"
    },
    {
      icon: <Truck className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Transportation & Logistics",
      shortDescription: "Secure transportation of hazardous materials with specialized vehicles and trained personnel",
      fullDescription: "Our specialized transportation fleet ensures safe movement of hazardous materials from your facility to our treatment centers. All vehicles are equipped with safety equipment, GPS tracking, and our drivers are trained in hazardous materials handling protocols.",
      features: [
        "DOT compliant vehicles",
        "GPS tracking and monitoring",
        "Trained hazmat drivers",
        "Emergency response protocols",
        "Real-time shipment tracking",
        "Insurance coverage"  
      ],
      pricing: "Custom pricing based on distance and volume"
    },
    {
      icon: <Flame className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Incineration Services",
      shortDescription: "High-temperature incineration for complete waste destruction and volume reduction",
      fullDescription: "Our state-of-the-art incineration facility operates at optimal temperatures to ensure complete destruction of organic waste materials. This process significantly reduces waste volume while generating energy that can be recovered for other uses.",
      features: [
        "High-temperature destruction",
        "Volume reduction up to 90%",
        "Energy recovery systems",
        "Emissions monitoring",
        "Ash residue management",
        "Environmental compliance"
      ],
      pricing: "UGX 2,500 per kilogram"
    },
    {
      icon: <Scissors className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Plastic Shredding",
      shortDescription: "Industrial plastic shredding and recycling services for sustainable waste management",
      fullDescription: "We provide comprehensive plastic waste shredding services that prepare materials for recycling. Our industrial shredders can handle various types of plastic waste, converting them into reusable raw materials for manufacturing.",
      features: [
        "Industrial-grade shredding",
        "Multiple plastic types accepted",
        "Quality control sorting",
        "Recycling preparation",
        "Baling and packaging",
        "Market-ready output"
      ],
      pricing: "UGX 800 per kilogram"
    },
    {
      icon: <Home className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Hoarding Cleanup",
      shortDescription: "Compassionate and professional hoarding cleanup services with discretion and care",
      fullDescription: "Our experienced team provides sensitive hoarding cleanup services, working with families and individuals to restore living spaces safely. We handle all types of accumulated materials with respect and professionalism.",
      features: [
        "Compassionate approach",
        "Biohazard cleaning",
        "Debris removal",
        "Sanitization services",
        "Structural assessment",
        "Family support coordination"
      ],
      pricing: "UGX 500,000 - 2,000,000 per project"
    },
    {
      icon: <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Training and Consultancy",
      shortDescription: "Professional training programs and consultancy services for waste management best practices",
      fullDescription: "We offer comprehensive training programs for healthcare facilities, institutions, and businesses on proper waste management practices, regulatory compliance, and safety protocols. Our consultancy services help organizations develop effective waste management strategies.",
      features: [
        "Customized training programs",
        "Regulatory compliance guidance",
        "Safety protocol development",
        "Waste audit services",
        "Policy development",
        "Ongoing support"
      ],
      pricing: "UGX 750,000 per training session"
    },
    {
      icon: <Monitor className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "e-Waste & Toxic Chemicals Management",
      shortDescription: "Responsible disposal and recycling of electronic waste and toxic chemical materials",
      fullDescription: "Our e-waste management program handles computers, phones, batteries, and other electronic equipment. We also manage toxic chemicals through proper neutralization, treatment, and disposal methods that protect the environment.",
      features: [
        "Data destruction services",
        "Component recovery",
        "Battery recycling",
        "Chemical neutralization",
        "Hazardous material treatment",
        "Certificate of destruction"
      ],
      pricing: "Variable based on item type and quantity"
    },
    {
      icon: <Calendar className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Expired Item Disposal",
      shortDescription: "Safe disposal of expired pharmaceuticals, food products, and other time-sensitive materials",
      fullDescription: "We specialize in the safe disposal of expired items including pharmaceuticals, food products, cosmetics, and other time-sensitive materials. Our process ensures that expired items are destroyed completely and cannot re-enter the supply chain.",
      features: [
        "Pharmaceutical destruction",
        "Food waste disposal",
        "Cosmetic product destruction",
        "Supply chain security",
        "Witness destruction option",
        "Documentation and reporting"
      ],
      pricing: "UGX 3,000 per kilogram"
    },
    {
      icon: <Shirt className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Textile Waste Treatment",
      shortDescription: "Sustainable treatment and recycling of textile waste from various industries",
      fullDescription: "Our textile waste treatment service handles fabric waste from manufacturing, retail, and consumer sources. We sort, process, and convert textile waste into new materials or energy, supporting the circular economy.",
      features: [
        "Textile sorting and grading",
        "Fiber recovery processes",
        "Dyeing waste treatment",
        "Fabric recycling",
        "Energy recovery from waste",
        "Sustainable disposal methods"
      ],
      pricing: "UGX 1,200 per kilogram"
    },
    {
      icon: <Bottle className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Sterilization of Bottles",
      shortDescription: "Professional bottle sterilization services for pharmaceutical and food industries",
      fullDescription: "We provide comprehensive bottle sterilization services using advanced steam sterilization and chemical treatment methods. This service is essential for pharmaceutical companies, food manufacturers, and beverage producers.",
      features: [
        "Steam sterilization",
        "Chemical treatment options",
        "Quality assurance testing",
        "Validation documentation",
        "Batch tracking",
        "Regulatory compliance"
      ],
      pricing: "UGX 50 per bottle"
    },
    {
      icon: <Package className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Supply of Bin Liners",
      shortDescription: "High-quality bin liners for medical, hazardous, and general waste management",
      fullDescription: "We supply a wide range of high-quality bin liners suitable for various waste management needs. Our bin liners are available in different sizes, thicknesses, and colors to help with waste segregation and proper disposal. Made from durable materials, they are leak-proof and tear-resistant, ensuring safe containment of all types of waste.",
      features: [
        "Available in various sizes and colors",
        "High-density polyethylene material",
        "Leak-proof and puncture-resistant",
        "Suitable for medical and hazardous waste",
        "Color-coded for waste segregation",
        "Custom printing available"
      ],
      pricing: "Starting from UGX 50,000 per roll (100 bags)"
    }
  ];

  const toggleService = (index: number) => {
    const isExpanding = expandedService !== index;
    setExpandedService(expandedService === index ? null : index);
    
    if (isExpanding) {
      // Use requestAnimationFrame to ensure the DOM has updated with the expanded content
      requestAnimationFrame(() => {
        // Find the service card element that was clicked
        const serviceCard = document.querySelector(`[data-service-index="${index}"]`);
        if (serviceCard) {
          // Calculate the offset to account for any fixed header
          const headerOffset = 120; // Adjust this value based on your header height
          const elementPosition = serviceCard.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          // Smooth scroll to the service card
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    }
  };

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <h1 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">Our Services</h1>
          <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-2 sm:px-4">
            Comprehensive waste management solutions tailored to meet your specific industry needs
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-10 sm:py-14 md:py-16 xl:py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12">
          <div className="space-y-3 sm:space-y-5 md:space-y-6">
            {services.map((service, index) => (
              <div key={index} data-service-index={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div 
                  className="p-3 sm:p-5 md:p-6 cursor-pointer"
                  onClick={() => toggleService(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
                      <div className="text-[#295cab] flex-shrink-0">{service.icon}</div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1">{service.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm md:text-base">{service.shortDescription}</p>
                      </div>
                    </div>
                    <div className="text-[#295cab] flex-shrink-0 ml-2">
                      {expandedService === index ? <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" /> : <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />}
                    </div>
                  </div>
                </div>
                
                {expandedService === index && (
                  <div className="px-3 sm:px-5 md:px-6 pb-4 sm:pb-6 border-t border-gray-100">
                    <div className="mt-3 sm:mt-5 md:mt-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                        <div className="lg:col-span-2">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Service Details</h4>
                          <p className="text-gray-600 mb-4 text-sm sm:text-base">{service.fullDescription}</p>

                        </div>
                        {(index === 0 || index === 1) && (
                          <div className="lg:col-span-1">
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <img 
                                src={`${import.meta.env.BASE_URL}${index === 0 ? 'biohaztruck.jpg' : 'companyassets.jpg'}`} 
                                alt={index === 0 
                                  ? "Our waste collection truck in operation" 
                                  : "Our transportation and logistics fleet"} 
                                className="w-full h-48 object-cover rounded-lg shadow-md"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  console.error('Failed to load image:', target.src);
                                }}
                              />
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                {index === 0 
                                  ? "Our team safely handling medical waste disposal"
                                  : "Our modern fleet for safe and reliable transportation"}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-6">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Key Features</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-[#295cab] rounded-full flex-shrink-0"></div>
                              <span className="text-gray-600 text-sm sm:text-base">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-[#295cab] to-[#1e4a8a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Need a Custom Solution?</h2>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            We understand that every business has unique waste management needs. Contact us to discuss a tailored solution for your organization.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 max-w-2xl mx-auto">
            <a 
              href="tel:+256393254458"
              className="flex items-center justify-center space-x-3 bg-white text-[#295cab] px-4 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-blue-50 transition-colors text-sm sm:text-base"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold">Call: +256 393 254458</span>
            </a>
            <a 
              href="mailto:info@biohazwastesolutions.com"
              className="flex items-center justify-center space-x-3 border-2 border-white text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-white hover:text-[#295cab] transition-colors text-sm sm:text-base"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold">Email Us</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;